import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SelectProviderComponent, ButtonGroupModule } from '@libs/ui-lib';
import { Provider } from '@ffd/api-client';
import { PerioExamTraversalStrategy, RecessionInputType } from '../perio-exam-screen/services/perio-exam/models';
import {
  PerioExamMissingToothModalComponent,
  PerioExamMissingToothModalData,
  PerioExamMissingToothModalPayload,
} from '../perio-exam-missing-tooth-modal';
import { PerioExamScreenService } from '../perio-exam-screen/services/perio-exam/perio-exam-screen.service';

import { PerioExamDirection, PerioExamDirectionSlots } from './components/perio-exam-direction-card/perio-exam-direction-card.component';
import { PerioExamDirectionCarouselComponent } from './components/perio-exam-direction-carousel';

export interface PerioExamSettingsModalPayload {
  traversalStrategy: PerioExamTraversalStrategy;
  missingTeeth: number[];
  provider: Provider | undefined;
  recessionInputType: RecessionInputType;
}

export interface PerioExamSettingsModalData {
  missingTeeth: number[];
  extractedTeeth: number[];
}

@Component({
  selector: 'lib-perio-exam-settings-modal',
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    SelectProviderComponent,
    PerioExamDirectionCarouselComponent,
    ButtonGroupModule,
  ],
  templateUrl: './perio-exam-settings-modal.component.html',
  styleUrl: './perio-exam-settings-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioExamSettingsModalComponent implements OnDestroy {
  private readonly dialogRef = inject(DialogRef<PerioExamSettingsModalPayload>);
  private readonly dialog = inject(Dialog);
  private readonly dialogData: PerioExamSettingsModalData = inject(DIALOG_DATA);
  private readonly perioExamScreenService = inject(PerioExamScreenService);

  private readonly provider = computed(() => this.perioExamScreenService.viewModel().provider);

  private perioExamMissingToothModalRef: DialogRef<PerioExamMissingToothModalPayload> | null = null;

  protected missingTeeth = this.dialogData.missingTeeth;
  protected extractedTeeth = this.dialogData.extractedTeeth;

  protected readonly traversalStrategy = computed(() => this.perioExamScreenService.viewModel().traversalStrategy);

  public ngOnDestroy(): void {
    this.perioExamMissingToothModalRef?.close();
  }

  protected openMissingToothModal(): void {
    this.perioExamMissingToothModalRef = this.dialog.open<PerioExamMissingToothModalPayload, PerioExamMissingToothModalData>(
      PerioExamMissingToothModalComponent,
      {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        panelClass: ['max-w-[98%]', 'min-w-[940px]', 'h-[87%]', '!absolute', 'top-[10%]'],
        disableClose: true,
        closeOnNavigation: true,
        data: {
          missingTeeth: this.missingTeeth,
          extractedTeeth: this.extractedTeeth,
        },
      },
    );

    this.perioExamMissingToothModalRef.closed.subscribe((result) => {
      if (result) {
        this.missingTeeth = result.missingTeeth;
      }
    });
  }

  protected perioSettingsModalForm = new FormGroup({
    provider: new FormControl<Provider | undefined>(this.provider(), { nonNullable: true }),
    traversalStrategy: new FormControl<PerioExamTraversalStrategy>(this.traversalStrategy(), { nonNullable: true }),
    recessionInputType: new FormControl<RecessionInputType>(this.perioExamScreenService.getDefaultRecessionInputType(), {
      nonNullable: true,
    }),
  });

  public mockPerioExamDirections: PerioExamDirection[] = [
    {
      id: 'archWise',
      available: true,
      items: [
        { slot: PerioExamDirectionSlots.First, label: '1', side: 'left' },
        { slot: PerioExamDirectionSlots.Second, label: '2', side: 'right' },
        { slot: PerioExamDirectionSlots.Third, label: '4', side: 'right' },
        { slot: PerioExamDirectionSlots.Fourth, label: '3', side: 'left' },
      ],
    },
    {
      id: 'crossArchSnakeReverse',
      available: false,
      items: [
        { slot: PerioExamDirectionSlots.First, label: '3', side: 'left' },
        { slot: PerioExamDirectionSlots.Second, label: '4', side: 'right' },
        { slot: PerioExamDirectionSlots.Third, label: '2', side: 'right' },
        { slot: PerioExamDirectionSlots.Fourth, label: '1', side: 'left' },
      ],
    },
    {
      id: 'buccalThenLingual',
      available: false,
      items: [
        { slot: PerioExamDirectionSlots.First, label: '1', side: 'left' },
        { slot: PerioExamDirectionSlots.Second, label: '3', side: 'left' },
        { slot: PerioExamDirectionSlots.Third, label: '4', side: 'right' },
        { slot: PerioExamDirectionSlots.Fourth, label: '2', side: 'right' },
      ],
    },
    {
      available: false,
      items: [
        { slot: PerioExamDirectionSlots.First, label: '1', side: 'left' },
        { slot: PerioExamDirectionSlots.Second, label: '3', side: 'left' },
        { slot: PerioExamDirectionSlots.Third, label: '4', side: 'right' },
        { slot: PerioExamDirectionSlots.Fourth, label: '2', side: 'right' },
      ],
    },
  ];

  protected getSaveButtonTranslationKey(): string {
    return 'perio_exam_settings_modal_save';
  }

  protected onSaveClick(): void {
    const { provider, traversalStrategy, recessionInputType } = this.perioSettingsModalForm.getRawValue();
    this.perioExamScreenService.setDefaultProvider(provider!);
    this.perioExamScreenService.setDefaultTraversalStrategy(traversalStrategy);
    this.perioExamScreenService.updateMissingTeeth(this.missingTeeth);
    this.perioExamScreenService.setDefaultRecessionInputType(recessionInputType);
    this.close();
  }

  protected onCancelClick(): void {
    this.dialogRef.close();
  }

  private close(): void {
    this.dialogRef.close({
      ...this.perioSettingsModalForm.getRawValue(),
      missingTeeth: this.missingTeeth,
    });
  }

  protected onProviderChange(provider: Provider): void {
    this.perioSettingsModalForm.patchValue({ provider });
  }

  protected onPerioExamTraversalStrategySelected(traversalStrategy: PerioExamTraversalStrategy): void {
    this.perioSettingsModalForm.patchValue({ traversalStrategy });
  }

  protected onRecessionInputTypeChange(recessionInputType: RecessionInputType): void {
    this.perioSettingsModalForm.patchValue({ recessionInputType });
  }

  protected readonly recessionInputOptions: Array<{ labelKey: string; value: RecessionInputType }> = [
    { labelKey: 'perio_exam_settings_modal_recession_central', value: 'central' },
    { labelKey: 'perio_exam_settings_modal_recession_all', value: 'all' },
  ];
}
